import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import permissionsService, { AzureADGroup, AzureADUser, AccessLevel } from '../services/permissions.service';
import { useVisibilityAwareInterval } from '../hooks/useVisibilityAwareInterval';

export interface ComponentPermission {
  componentId: string;
  componentName: string;
  permissions: {
    [entityId: string]: {
      entityType: 'group' | 'user';
      accessLevel: number;
    };
  };
}

export interface SelectedEntity {
  type: 'group' | 'user';
  id: string;  // group_id or user_id
  label: string;  // display name
}

interface PermissionsModeContextType {
  permissionsMode: boolean;
  setPermissionsMode: (enabled: boolean) => void;
  enabledGroups: AzureADGroup[];
  enabledUsers: AzureADUser[];
  selectedEntity: SelectedEntity | null;
  setSelectedEntity: (entity: SelectedEntity | null) => void;
  // Legacy support for existing code
  selectedGroup: AzureADGroup | null;
  setSelectedGroup: (group: AzureADGroup | null) => void;
  /**
   * Full component_permissions dump — every component × every entity. Only
   * readable by admin groups, and only loaded while permissions mode is active,
   * because it exists for *editing* permissions. Runtime gating must not depend
   * on it (see `componentAccessLevels`).
   */
  componentPermissions: Map<string, ComponentPermission>;
  savedPermissions: Map<string, ComponentPermission>;
  /**
   * True once the full permission dump has loaded successfully. While false,
   * savePermissions refuses to run (the map is a partial state and posting it
   * would overwrite grants that never loaded — the 2026-08-13 prod wipe).
   * The toolbar disables its Save button off this.
   */
  permissionsBaselineLoaded: boolean;
  /**
   * The current user's own effective access level per component, resolved
   * server-side (highest-wins across their groups + any user grant). This is the
   * authoritative source for runtime gating. `null` means "not loaded yet" —
   * consumers fall back to component defaults rather than assuming no access.
   */
  componentAccessLevels: Record<string, number> | null;
  updateComponentPermission: (
    componentId: string,
    componentName: string,
    entityId: string,
    entityType: 'group' | 'user',
    accessLevel: number
  ) => void;
  bulkUpdatePermissions: (
    components: Array<{ id: string; name: string }>,
    entityId: string,
    entityType: 'group' | 'user',
    accessLevel: number
  ) => void;
  /**
   * Drop an entity's grant on a component from the working map (staged, like every
   * other edit: it is persisted by savePermissions). The component entry is kept even
   * when it ends up empty, so the save can name it for deletion on the server.
   */
  removeComponentPermission: (componentId: string, entityId: string) => void;
  savePermissions: () => Promise<void>;
  loadingGroups: boolean;
  loadingUsers: boolean;
  pageComponents: Array<{ id: string; name: string }>;
  setPageComponents: React.Dispatch<React.SetStateAction<Array<{ id: string; name: string }>>>;
  accessLevels: AccessLevel[];
}

const PermissionsModeContext = createContext<PermissionsModeContextType | undefined>(undefined);

const ENABLED_GROUPS_CACHE_KEY = 'gwi360_enabled_groups_cache';

/**
 * How often an open tab re-reads the current user's effective component access
 * levels. Permission changes used to require a new session to take effect: the
 * levels were fetched exactly once per page load and any failure was swallowed,
 * so one bad response left gating wrong until the user signed in again.
 */
const ACCESS_LEVEL_REFRESH_MS = 5 * 60 * 1000;

/** Backoff for retrying a failed load, so a transient error can't strand the session. */
const ACCESS_LEVEL_RETRY_MS = [2_000, 10_000, 30_000];

const getCachedEnabledGroups = (): AzureADGroup[] => {
  try {
    const cached = localStorage.getItem(ENABLED_GROUPS_CACHE_KEY);
    return cached ? JSON.parse(cached) : [];
  } catch {
    return [];
  }
};

const setCachedEnabledGroups = (groups: AzureADGroup[]): void => {
  try {
    localStorage.setItem(ENABLED_GROUPS_CACHE_KEY, JSON.stringify(groups));
  } catch {
    // localStorage might be full or disabled, ignore
  }
};

export const PermissionsModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [permissionsMode, setPermissionsMode] = useState(false);
  const [enabledGroups, setEnabledGroups] = useState<AzureADGroup[]>(getCachedEnabledGroups);
  const [enabledUsers, setEnabledUsers] = useState<AzureADUser[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<SelectedEntity | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<AzureADGroup | null>(null);  // Legacy support
  const [componentPermissions, setComponentPermissions] = useState<Map<string, ComponentPermission>>(new Map());
  const [savedPermissions, setSavedPermissions] = useState<Map<string, ComponentPermission>>(new Map());
  // True once the full permission dump has loaded successfully at least once.
  // savePermissions posts the ENTIRE map as the new table state, so saving from
  // a session whose baseline never loaded would destroy every grant not in the
  // map — that is exactly the 2026-08-13 prod wipe. Saves are blocked until
  // this flips true.
  const [baselineLoaded, setBaselineLoaded] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [pageComponents, setPageComponents] = useState<Array<{ id: string; name: string }>>([]);
  const [accessLevels, setAccessLevels] = useState<AccessLevel[]>([]);
  const [componentAccessLevels, setComponentAccessLevels] = useState<Record<string, number> | null>(null);
  const componentPermissionsAbortRef = useRef<AbortController | null>(null);
  const accessLevelsAbortRef = useRef<AbortController | null>(null);
  const accessLevelRetryRef = useRef<{ attempt: number; timer: number | null }>({ attempt: 0, timer: null });
  const modeAbortRef = useRef<AbortController | null>(null);
  const editsSinceLoadRef = useRef(false);

  const { accounts } = useMsal();
  const isAuthenticated = accounts.length > 0;

  /**
   * Load this user's effective component access levels. Self-scoped, so every
   * authenticated user can read their own — unlike the full permission dump,
   * which needs an admin grant and used to 403 (silently) for most groups.
   */
  const loadComponentAccessLevels = useCallback(async () => {
    accessLevelsAbortRef.current?.abort();
    const controller = new AbortController();
    accessLevelsAbortRef.current = controller;

    try {
      const levels = await permissionsService.getUserComponentAccessLevels(controller.signal);
      setComponentAccessLevels(levels || {});
      accessLevelRetryRef.current.attempt = 0;
    } catch (error: any) {
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') return;

      // A 403 is an authoritative answer, not a transient failure — retrying it
      // just makes noise. The endpoint is a bypass path, so this only happens for
      // token/scope problems (e.g. a guard session), where the legacy client-side
      // fallback in useResolvedAccessLevel applies.
      if (error?.response?.status === 403) {
        console.warn('Component access levels denied; falling back to component defaults');
        return;
      }

      // Retry rather than leaving gating stuck on component defaults for the rest
      // of the session. The regular refresh interval takes over once retries are
      // exhausted, so the tab still recovers on its own.
      const { attempt } = accessLevelRetryRef.current;
      const delay = ACCESS_LEVEL_RETRY_MS[attempt];
      console.warn(
        `Failed to load component access levels (attempt ${attempt + 1})` +
        `${delay ? `, retrying in ${delay}ms` : '; waiting for the next refresh'}`,
        error?.response?.status ?? error,
      );
      if (delay !== undefined) {
        accessLevelRetryRef.current.attempt = attempt + 1;
        accessLevelRetryRef.current.timer = window.setTimeout(loadComponentAccessLevels, delay);
      }
    }
  }, []);

  // Effective access levels: load once authenticated, and re-load if the account
  // changes. Depends on MSAL accounts rather than reading them synchronously, so
  // the fetch can't be skipped by losing a race with MSAL initialization.
  useEffect(() => {
    if (!isAuthenticated) {
      setComponentAccessLevels(null);
      return;
    }

    loadComponentAccessLevels();

    return () => {
      accessLevelsAbortRef.current?.abort();
      if (accessLevelRetryRef.current.timer !== null) {
        clearTimeout(accessLevelRetryRef.current.timer);
        accessLevelRetryRef.current.timer = null;
      }
      accessLevelRetryRef.current.attempt = 0;
    };
  }, [isAuthenticated, accounts[0]?.localAccountId, loadComponentAccessLevels]);

  // Revalidate in the background so a permission change lands in an open tab
  // without a reload. Paused while the tab is hidden; fires on return to it.
  useVisibilityAwareInterval(
    loadComponentAccessLevels,
    isAuthenticated ? ACCESS_LEVEL_REFRESH_MS : null,
  );

  // Enabled groups drive the permissions-mode button visibility, so they're
  // needed outside of permissions mode.
  useEffect(() => {
    if (isAuthenticated && enabledGroups.length === 0) {
      fetchEnabledGroups();
    }
  }, [isAuthenticated]);

  // Fetch enabled groups, users, access levels and the full permission dump when
  // permissions mode is activated. The dump is admin-only and exists purely for
  // editing, so it is NOT loaded for ordinary page views.
  useEffect(() => {
    if (permissionsMode) {
      fetchEnabledGroups();
      fetchEnabledUsers();
      fetchAccessLevels();
      loadComponentPermissions();
    }

    return () => {
      modeAbortRef.current?.abort();
      componentPermissionsAbortRef.current?.abort();
    };
  }, [permissionsMode]);

  const loadComponentPermissions = async () => {
    componentPermissionsAbortRef.current?.abort();
    const controller = new AbortController();
    componentPermissionsAbortRef.current = controller;
    // The dump now loads when permissions mode is activated rather than at mount,
    // so an admin can start editing while it's still in flight. Applying the
    // response then would silently revert their edit.
    editsSinceLoadRef.current = false;

    try {
      const permissions = await permissionsService.getComponentPermissions(controller.signal);
      if (editsSinceLoadRef.current) return;

      // Convert backend format to Map format
      const permissionsMap = new Map<string, ComponentPermission>();

      permissions.forEach(perm => {
        const existing = permissionsMap.get(perm.component_id);

        // Use entity-specific ID as the key
        const entityId = perm.entity_type === 'group' ? perm.group_id! : perm.user_id!;

        if (existing) {
          // Add permissions for this entity to existing component
          existing.permissions[entityId] = {
            entityType: perm.entity_type,
            accessLevel: perm.access_level
          };
        } else {
          // Create new component entry
          permissionsMap.set(perm.component_id, {
            componentId: perm.component_id,
            componentName: perm.component_name,
            permissions: {
              [entityId]: {
                entityType: perm.entity_type,
                accessLevel: perm.access_level
              }
            }
          });
        }
      });

      setComponentPermissions(permissionsMap);
      // Also save as the baseline for detecting changes
      setSavedPermissions(new Map(permissionsMap));
      setBaselineLoaded(true);
    } catch (error: any) {
      // Ignore abort errors - expected when we cancel stale requests
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return;
      }
      // Editing-only data: permissions mode degrades to "nothing configured"
      // rather than breaking the page. Runtime gating is unaffected — it reads
      // componentAccessLevels. Logged because a 403 here means the admin is
      // missing the grant on GET /permissions/components/permissions.
      console.warn('Failed to load component permissions for permissions mode',
        error?.response?.status ?? error);
    }
  };

  const fetchEnabledGroups = async () => {
    modeAbortRef.current?.abort();
    const controller = new AbortController();
    modeAbortRef.current = controller;

    try {
      setLoadingGroups(true);
      const groups = await permissionsService.getEnabledGroups(controller.signal);
      setEnabledGroups(groups);
      setCachedEnabledGroups(groups);

      // Auto-select first group if available (legacy support)
      if (groups.length > 0 && !selectedGroup) {
        setSelectedGroup(groups[0]);
      }
    } catch (error: any) {
      // Ignore abort errors
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return;
      }
    } finally {
      setLoadingGroups(false);
    }
  };

  const fetchEnabledUsers = async () => {
    // Share the same controller as fetchEnabledGroups since they run together
    const controller = modeAbortRef.current || new AbortController();

    try {
      setLoadingUsers(true);
      const users = await permissionsService.getEnabledUsers(controller.signal);
      setEnabledUsers(users);
    } catch (error: any) {
      // Ignore abort errors
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return;
      }
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchAccessLevels = async () => {
    try {
      const levels = await permissionsService.getAccessLevels();
      setAccessLevels(levels);
    } catch (error: any) {
      // Ignore abort errors
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return;
      }
    }
  };

  const updateComponentPermission = (
    componentId: string,
    componentName: string,
    entityId: string,
    entityType: 'group' | 'user',
    accessLevel: number
  ) => {
    editsSinceLoadRef.current = true;
    setComponentPermissions((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(componentId);

      // Create a new component entry with deep copy of existing permissions
      const newComponentEntry = {
        componentId,
        componentName,
        permissions: existing ? { ...existing.permissions } : {},
      };

      // Set the access level for this entity
      newComponentEntry.permissions[entityId] = { entityType, accessLevel };
      newMap.set(componentId, newComponentEntry);

      return newMap;
    });
  };

  const bulkUpdatePermissions = (
    components: Array<{ id: string; name: string }>,
    entityId: string,
    entityType: 'group' | 'user',
    accessLevel: number
  ) => {
    editsSinceLoadRef.current = true;
    setComponentPermissions((prev) => {
      const newMap = new Map(prev);

      components.forEach((component) => {
        // Get existing or create new component entry with deep copy of permissions
        const existing = newMap.get(component.id);
        const newComponentEntry = {
          componentId: component.id,
          componentName: component.name,
          permissions: existing ? { ...existing.permissions } : {},
        };

        // Set the access level for this entity
        newComponentEntry.permissions[entityId] = { entityType, accessLevel };
        newMap.set(component.id, newComponentEntry);
      });

      return newMap;
    });
  };

  const removeComponentPermission = (componentId: string, entityId: string) => {
    editsSinceLoadRef.current = true;
    setComponentPermissions((prev) => {
      const existing = prev.get(componentId);
      if (!existing || !(entityId in existing.permissions)) return prev;
      const permissions = { ...existing.permissions };
      delete permissions[entityId];
      const newMap = new Map(prev);
      newMap.set(componentId, { ...existing, permissions });
      return newMap;
    });
  };

  const savePermissions = async () => {
    // HARD guard: the backend replaces the stored permissions of every
    // component in the payload, and the payload is this map. If the baseline
    // dump never loaded, the map holds only this session's edits — saving it
    // would wipe every other grant (2026-08-13 prod incident). Refuse loudly.
    if (!baselineLoaded) {
      window.alert(
        'The existing permissions never finished loading, so saving now would ' +
        'overwrite grants that are not visible in this session. ' +
        'Exit permissions mode and try again.',
      );
      return;
    }
    try {
      // Convert Map format to backend format
      const permissionsToSave = Array.from(componentPermissions.values()).flatMap(comp =>
        Object.entries(comp.permissions).map(([entityId, perms]) => {
          const payload: any = {
            component_id: comp.componentId,
            component_name: comp.componentName,
            entity_type: perms.entityType,
            access_level: perms.accessLevel,
          };

          // Only include group_id or user_id based on entity_type
          if (perms.entityType === 'group') {
            payload.group_id = entityId;
          } else if (perms.entityType === 'user') {
            payload.user_id = entityId;
          }

          return payload;
        })
      );

      // Components whose every grant was removed have no row above; name them so the
      // server includes them in the replace scope (otherwise their rows would survive).
      const cleared = Array.from(componentPermissions.values())
        .filter((comp) => Object.keys(comp.permissions).length === 0)
        .map((comp) => comp.componentId);

      await permissionsService.saveComponentPermissions(permissionsToSave, cleared);

      // Update saved permissions to match current state
      setSavedPermissions(new Map(componentPermissions));

      // Re-read the saver's own effective levels so the change is visible without
      // a reload (their edit may have changed their own access).
      loadComponentAccessLevels();
    } catch (error) {
      // A swallowed failure here reads as "saved" to the admin — say it failed.
      console.error('Failed to save component permissions', error);
      window.alert('Saving permissions FAILED — your changes were not saved.');
    }
  };

  return (
    <PermissionsModeContext.Provider
      value={{
        permissionsMode,
        setPermissionsMode,
        enabledGroups,
        enabledUsers,
        selectedEntity,
        setSelectedEntity,
        selectedGroup,  // Legacy support
        setSelectedGroup,  // Legacy support
        componentPermissions,
        savedPermissions,
        permissionsBaselineLoaded: baselineLoaded,
        componentAccessLevels,
        updateComponentPermission,
        bulkUpdatePermissions,
        removeComponentPermission,
        savePermissions,
        loadingGroups,
        loadingUsers,
        pageComponents,
        setPageComponents,
        accessLevels,
      }}
    >
      {children}
    </PermissionsModeContext.Provider>
  );
};

export const usePermissionsMode = (): PermissionsModeContextType => {
  const context = useContext(PermissionsModeContext);
  if (!context) {
    throw new Error('usePermissionsMode must be used within a PermissionsModeProvider');
  }
  return context;
};

/**
 * Non-throwing variant for components that should degrade gracefully when
 * rendered outside a PermissionsModeProvider (e.g. tests, isolated previews).
 * Returns `undefined` when no provider is in the tree.
 */
export const useTryPermissionsMode = (): PermissionsModeContextType | undefined => {
  return useContext(PermissionsModeContext);
};
